import React, { useEffect, useMemo } from 'react'
import CircularProgress from '@mui/material/CircularProgress'
import Pagination from '@mui/material/Pagination'
import Typography from '@mui/material/Typography'
import { useTheme, Box } from '@mui/material'
import AccordionComponent from './Accordion'
import LinkConverter from '../../../LinkConverter'
import { useGetFasDataHook, wellKnownGeography } from '../utils'
import {
  useReactTable,
  ColumnDef,
  Row,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
} from '@tanstack/react-table'
import { FasData, FasDataItem, FasDataItemPlanningStatus } from '../types'
import AlertBanner from '../../../AlertBanner'

type IssueListWithPaginationProps = {
  selectedGeoFilters: number
  selectedPlanningStatus: FasDataItemPlanningStatus
  globalTextSearch: string
}

interface RenderTableProps extends IssueListWithPaginationProps {
  data: FasData['all']
  columns: ColumnDef<FasDataItem>[]
}

export const IssueListWithPagination = ({
  globalTextSearch,
  selectedGeoFilters,
  selectedPlanningStatus,
}: IssueListWithPaginationProps) => {
  const { fasData, fasDataFetchError, isLoadingFasData } = useGetFasDataHook()
  const excludedIncidents = ['High', 'Critical']
  const filteredFasData = fasData?.all.filter(item => !excludedIncidents.includes(item.hightestLevel))
  const columns = useMemo<ColumnDef<FasDataItem>[]>(
    () => [
      {
        header: 'Issues',
        columns: [
          {
            header: 'title',
            accessorKey: 'title',
          },
          {
            id: 'date',
            header: 'Dato',
            accessorFn: row => row['plannedStart'] || row['occured'],
            cell: row => row.getValue(),
            sortDescFirst: true,
            disableGlobalFilter: true,
          },
          {
            header: 'planningStatus',
            accessorKey: 'planningStatus',
            enableSorting: false,
            enableGlobalFilter: false,
            filterFn: 'equals',
          },
          {
            header: 'Funktion',
            accessorKey: 'function',
            enableGlobalFilter: false,
          },
          {
            accessorKey: 'geographyFlag',
            filterFn: (row: Row<FasDataItem>, _id: string, value: number) => {
              return wellKnownGeography.isFlagged(value, row.original.geographyFlag) === true
            },
          },
        ],
      },
    ],
    [],
  )

  if (isLoadingFasData || fasDataFetchError) {
    return isLoadingFasData ? (
      <CircularProgress color="inherit" />
    ) : (
      <AlertBanner
        severity="error"
        id="driftsinformation-error-alert"
        title="Noget gik galt!"
        description="Prøv at opdatere siden"
        inlineAlert={true}
      />
    )
  }

  return (
    <div>
      {filteredFasData && (
        <RenderTable
          data={filteredFasData}
          columns={columns}
          globalTextSearch={globalTextSearch}
          selectedGeoFilters={selectedGeoFilters}
          selectedPlanningStatus={selectedPlanningStatus}
        />
      )}
    </div>
  )
}

const RenderTable = ({
  data,
  columns,
  globalTextSearch,
  selectedGeoFilters,
  selectedPlanningStatus,
}: RenderTableProps) => {
  const labels = new Map([
    [FasDataItemPlanningStatus.Unplanned, 'aktuelle'],
    [FasDataItemPlanningStatus.Planned, 'planlagte'],
    [FasDataItemPlanningStatus.Completed, 'løste'],
  ])

  const { setGlobalFilter, getRowModel, getState, getPageCount, setPageIndex, setColumnFilters } = useReactTable({
    data,
    columns,
    initialState: {
      pagination: {
        pageSize: 8,
      },
    },
    state: {
      sorting: [{ id: 'date', desc: true }],
    },
    autoResetAll: false,
    getRowId: origRow => origRow.fasId,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
  })
  const { palette } = useTheme()
  const styles = {
    pagination: {
      marginTop: '24px',
      '& > .MuiPagination-ul': {
        justifyContent: 'center',
        '& > li': {
          '& .MuiPaginationItem-textPrimary.Mui-selected': {
            backgroundColor: palette.grey[300],
            color: palette.common.black,
          },
        },
      },
    },
    zeroResultsWrapper: {
      '& p': {
        marginBottom: '1rem',
      },
      '& a': {
        fontSize: 'inherit',
      },
    },
  }
  const { pageIndex } = getState().pagination

  const handlePagination = (_event: React.ChangeEvent<unknown>, value: number) => {
    setPageIndex(value - 1)
  }

  useEffect(() => {
    setColumnFilters([
      { id: 'planningStatus', value: selectedPlanningStatus },
      { id: 'geographyFlag', value: selectedGeoFilters },
    ])
  }, [selectedGeoFilters, selectedPlanningStatus])

  useEffect(() => {
    setGlobalFilter(globalTextSearch)
    setPageIndex(0)
  }, [setGlobalFilter, globalTextSearch])

  if (getRowModel().rows.length === 0) {
    return (
      <Box sx={styles.zeroResultsWrapper}>
        <Typography variant="body2">
          Vi har ikke registreret nogen {labels.get(selectedPlanningStatus)} driftsforstyrrelser i det valgte område.
        </Typography>
        <Typography variant="body2">
          Kontakt os her, hvis du stadig oplever problemer:{' '}
          <LinkConverter linkHref={'/kundeservice/kontakt/'}>tdc.dk/kundeservice/kontakt/</LinkConverter>
        </Typography>
      </Box>
    )
  }

  return (
    <>
      {getRowModel().rows.map(row => (
        <AccordionComponent key={row.id} {...row.original} />
      ))}
      <Pagination onChange={handlePagination} page={pageIndex + 1} sx={styles.pagination} count={getPageCount()} />
    </>
  )
}
